import React from 'react';
import { Message as MessageType } from '../../types/chat';
import { AlertTriangle, MessageSquare } from 'lucide-react';

function formatMessageTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

interface MessageProps {
  message: MessageType;
  isOwn?: boolean;
}

export function Message({ message, isOwn }: MessageProps) {
  const formattedTime = formatMessageTime(message.createdAt);

  const getPriorityClass = () => {
    if (!message.metadata?.priority) return '';
    switch (message.metadata.priority) {
      case 'high': return 'bg-red-50 border-red-200';
      case 'medium': return 'bg-amber-50 border-amber-200';
      case 'low': return 'bg-blue-50 border-blue-200';
      default: return '';
    }
  };

  return (
    <div className="flex justify-center mb-4 px-1.5 sm:px-0">
      <div className="w-full max-w-[90%] sm:max-w-[75%]">
        <div className={`
          rounded-lg px-3 py-2 shadow-sm
          ${message.type === 'broadcast' 
            ? message.isAdmin
              ? 'bg-green-600 text-white border border-green-700'
              : 'bg-amber-50 border border-amber-200'
            : message.isAdmin
              ? 'bg-green-600 text-white border border-green-700'
              : isOwn
                ? 'bg-primary-50 border border-primary-200'
                : 'bg-gray-50 border border-gray-200'}
          ${getPriorityClass()}
        `}>
          <div className="flex items-center gap-1.5 mb-1 justify-between">
            <span className="font-medium text-sm">
              {message.senderName}
              {message.isAdmin && message.type !== 'broadcast' && (
                <span className="ml-1.5 text-xs bg-white/20 text-white px-1.5 py-0.5 rounded-full">
                  Admin
                </span>
              )}
            </span>
            <div className="flex items-center gap-1.5">
              {message.type === 'broadcast' && (
                <AlertTriangle className={`w-3.5 h-3.5 ${message.isAdmin ? 'text-white/80' : 'text-amber-500'}`} />
              )}
              {message.type === 'chat' && (
                <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
              )}
            </div>
          </div>
          
          <p className={`whitespace-pre-wrap break-words ${
            message.isAdmin || (message.type === 'broadcast' && message.isAdmin) ? 'text-white' : 'text-gray-800'
          } text-sm sm:text-base overflow-hidden`}>
            {message.content}
          </p>
          
          <div className="flex justify-between items-center mt-2">
            {formattedTime && (
              <span className={`text-xs ${
                message.isAdmin || (message.type === 'broadcast' && message.isAdmin) ? 'text-white/80' : 'text-gray-500'
              }`}>{formattedTime}</span>
            )}
            <div className="flex items-center gap-2">
            {message.metadata?.category && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                message.isAdmin || (message.type === 'broadcast' && message.isAdmin)
                  ? 'bg-white/20 text-white' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {message.metadata.category}
              </span>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}